import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ReactRendererService from 'garaje/services/react-renderer';
import type { Root } from 'react-dom/client';

interface ThreatIntelligenceSettingsArgs {
  locationId: string;
}

interface EligibleEmployeeData {
  id: string;
  type: string;
  attributes: {
    name: string;
    [key: string]: unknown;
  };
}

interface EligibleEmployeesResponse {
  data: EligibleEmployeeData[];
}

interface AdminOption {
  value: string;
  label: string;
}

/**
 * Glimmer wrapper component for the React ThreatIntelligenceSettings component.
 * Displays on the /communications/settings page as an expandable panel.
 */
export default class ThreatIntelligenceSettingsComponent extends Component<ThreatIntelligenceSettingsArgs> {
  @service declare ajax: AjaxService;
  @service declare reactRenderer: ReactRendererService;
  @service declare flashMessages: FlashMessagesService;

  @tracked isPanelOpen = false;
  private root?: Root;

  /**
   * Search eligible employees for proactive alert recipients.
   * Matches the ERT pattern exactly - uses the same API endpoint.
   * Task is restartable to cancel previous searches on new input.
   */
  searchEligibleEmployees = task({ restartable: true }, async (query: string): Promise<AdminOption[]> => {
    const url = `/a/visitors/api/v3/locations/${this.args.locationId}/emergency-notification-eligible-employees`;
    const response = await this.ajax.request<EligibleEmployeesResponse>(url, {
      data: {
        'filter[name]': query,
      },
    });

    return response.data.map((employeeData) => ({
      value: employeeData.id,
      label: employeeData.attributes.name,
    }));
  });

  /**
   * Resolve recipient IDs to full employee data (for edit mode).
   * Uses the same API endpoint but filters by ID instead of name.
   * Similar to LERT pattern: store.query('employee', { filter: { id: ids.join(',') } })
   */
  resolveRecipientsByIds = task(async (ids: string[]): Promise<AdminOption[]> => {
    if (!ids.length) return [];

    const url = `/a/visitors/api/v3/locations/${this.args.locationId}/emergency-notification-eligible-employees`;
    const response = await this.ajax.request<EligibleEmployeesResponse>(url, {
      data: {
        'filter[id]': ids.join(','),
      },
    });

    return response.data.map((employeeData) => ({
      value: employeeData.id,
      label: employeeData.attributes.name,
    }));
  });

  /**
   * Wrapper function to call the search task from React.
   * This is passed as onSearchRecipients prop to the React component.
   */
  onSearchRecipients = async (query: string): Promise<AdminOption[]> => {
    return this.searchEligibleEmployees.perform(query);
  };

  /**
   * Wrapper function to resolve recipient IDs from React.
   * This is passed as onResolveRecipients prop to the React component.
   */
  onResolveRecipients = async (ids: string[]): Promise<AdminOption[]> => {
    return this.resolveRecipientsByIds.perform(ids);
  };

  @action
  startEditing(): void {
    this.isPanelOpen = true;
  }

  @action
  cancelEditing(): void {
    this.isPanelOpen = false;
  }

  @action
  async injectComponent(): Promise<void> {
    const element = document.getElementById('react__threat-intelligence-settings');
    if (!element) {
      return;
    }

    try {
      const { ThreatIntelligenceSettings } = await import(
        'reactRichComponents/communication/ThreatIntelligenceSettings'
      );

      if (!ThreatIntelligenceSettings || typeof ThreatIntelligenceSettings !== 'function') {
        // eslint-disable-next-line no-console
        console.error('[ThreatIntelligenceSettings] Component not found. Available exports:', Object.keys(module));
        this.flashMessages.showAndHideFlash('error', 'Threat intelligence settings component not available');
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.root = await this.reactRenderer.render(ThreatIntelligenceSettings, 'react__threat-intelligence-settings', {
        locationId: this.args.locationId,
        onSuccess: () => this.onSuccess(),
        onError: (error: Error) => this.onError(error),
        onSearchRecipients: this.onSearchRecipients,
        onResolveRecipients: this.onResolveRecipients,
      });
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Failed to load threat intelligence settings');
    }
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }

  @action
  onSuccess(): void {
    this.flashMessages.showAndHideFlash('success', 'Threat intelligence settings saved successfully');
  }

  @action
  onError(_error: Error): void {
    this.flashMessages.showAndHideFlash('error', 'Failed to save threat intelligence settings');
  }
}
